export const MAX_COMMENT_NAME_LENGTH = 80;
export const MAX_COMMENT_BODY_LENGTH = 1000;
export const MAX_COMMENT_EMAIL_LENGTH = 254;
export const MAX_TIMESTAMP_SECONDS = 24 * 60 * 60;

// Reviewer-facing feedback categories. Values match the server enum.
export const COMMENT_CATEGORIES = Object.freeze([
  { value: "voiceover", label: "Voiceover" },
  { value: "captions", label: "Captions" },
  { value: "on_screen_text", label: "On screen text" },
  { value: "other", label: "Other" },
]);
export const DEFAULT_COMMENT_CATEGORY = "other";
const CATEGORY_VALUES = new Set(COMMENT_CATEGORIES.map((entry) => entry.value));

export const commentCategoryLabel = (value) =>
  COMMENT_CATEGORIES.find((entry) => entry.value === value)?.label || "Other";

// Accepts "mm:ss", "h:mm:ss", or a bare number of seconds.
export const parseTimestampInput = (value) => {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  if (/^\d+$/.test(trimmed)) return Number(trimmed);

  const parts = trimmed.split(":");
  if (parts.length < 2 || parts.length > 3) return null;
  if (!parts.every((part) => /^\d{1,2}$/.test(part))) return null;

  const seconds = Number(parts.pop());
  const minutes = Number(parts.pop());
  const hours = parts.length ? Number(parts.pop()) : 0;
  if (seconds > 59 || minutes > 59) return null;

  return hours * 3600 + minutes * 60 + seconds;
};

export const formatTimestamp = (seconds) => {
  if (!Number.isFinite(seconds) || seconds < 0) return "";
  const total = Math.round(seconds);
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const secs = total % 60;
  const pad = (n) => String(n).padStart(2, "0");
  return hours > 0
    ? `${hours}:${pad(minutes)}:${pad(secs)}`
    : `${pad(minutes)}:${pad(secs)}`;
};

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

const isId = (value) => typeof value === "string" || typeof value === "number";

export const characterLength = (value) => Array.from(value).length;

export const isPublicCommentRecord = (comment) => {
  if (!comment || typeof comment !== "object" || Array.isArray(comment)) {
    return false;
  }
  if (!isId(comment.id)) return false;
  if (typeof comment.name !== "string" || typeof comment.body !== "string") {
    return false;
  }
  if (
    comment.parentCommentId !== undefined &&
    comment.parentCommentId !== null &&
    !isId(comment.parentCommentId)
  ) {
    return false;
  }
  if (comment.category !== undefined && !CATEGORY_VALUES.has(comment.category)) {
    return false;
  }
  if (
    comment.timestampSeconds !== undefined &&
    comment.timestampSeconds !== null &&
    (typeof comment.timestampSeconds !== "number" ||
      !Number.isFinite(comment.timestampSeconds) ||
      comment.timestampSeconds < 0)
  ) {
    return false;
  }
  if (comment.replies !== undefined) {
    if (!Array.isArray(comment.replies)) return false;
    if (comment.replies.some((reply) => !isPublicCommentRecord(reply))) {
      return false;
    }
  }
  return true;
};

const keyForId = (id) => String(id);

const parentKeyFor = (comment) =>
  comment.parentCommentId === undefined || comment.parentCommentId === null
    ? null
    : keyForId(comment.parentCommentId);

// Accepts flat records (parentCommentId) or nested replies, and always returns
// one display level of replies under each top-level comment.
export const buildPublicCommentThread = (comments) => {
  if (!Array.isArray(comments)) return [];

  const recordsById = new Map();
  const parentKeyByKey = new Map();
  const orderedKeys = [];

  const visit = (comment, containerKey) => {
    if (!isPublicCommentRecord(comment)) return;
    const key = keyForId(comment.id);
    if (recordsById.has(key)) return;

    recordsById.set(key, comment);
    parentKeyByKey.set(key, containerKey ?? parentKeyFor(comment));
    orderedKeys.push(key);

    if (Array.isArray(comment.replies)) {
      comment.replies.forEach((reply) => visit(reply, key));
    }
  };

  comments.forEach((comment) => visit(comment, null));

  const findTopLevelKey = (key) => {
    const visited = new Set([key]);
    let current = key;
    for (;;) {
      const parentKey = parentKeyByKey.get(current);
      if (parentKey === null || parentKey === undefined) return current;
      if (visited.has(parentKey)) return null;
      visited.add(parentKey);
      current = parentKey;
    }
  };

  const topLevelKeyByKey = new Map();
  orderedKeys.forEach((key) => {
    const topLevelKey = findTopLevelKey(key);
    topLevelKeyByKey.set(
      key,
      !topLevelKey || !recordsById.has(topLevelKey) ? key : topLevelKey,
    );
  });

  // Display nodes are rebuilt from an explicit whitelist; commenter email is
  // never copied into anything the public page can render. Ownership fields ride
  // along because only the author may edit or delete, and only the API can prove
  // ownership, so they pass through untouched.
  const ownershipFields = (comment) => ({
    languageCode: comment.languageCode ?? "en",
    parentCommentId: comment.parentCommentId ?? null,
    isOwn: comment.isOwn === true,
    isAdmin: comment.isAdmin === true,
    resolved: comment.resolved === true,
    ...(comment.isOwn === true ? { editedAt: comment.editedAt ?? null } : {}),
  });

  const thread = [];
  const threadByKey = new Map();

  orderedKeys.forEach((key) => {
    if (topLevelKeyByKey.get(key) !== key) return;
    const comment = recordsById.get(key);
    const node = {
      id: comment.id,
      name: comment.name,
      body: comment.body,
      category: comment.category ?? DEFAULT_COMMENT_CATEGORY,
      timestampSeconds: comment.timestampSeconds ?? null,
      ...ownershipFields(comment),
      replies: [],
    };
    thread.push(node);
    threadByKey.set(key, node);
  });

  orderedKeys.forEach((key) => {
    const topLevelKey = topLevelKeyByKey.get(key);
    if (topLevelKey === key) return;

    const comment = recordsById.get(key);
    const reply = {
      id: comment.id,
      name: comment.name,
      body: comment.body,
      category: comment.category ?? DEFAULT_COMMENT_CATEGORY,
      timestampSeconds: comment.timestampSeconds ?? null,
      ...ownershipFields(comment),
    };
    const parentNode = threadByKey.get(topLevelKey);
    if (parentNode) {
      parentNode.replies.push(reply);
    } else {
      const node = { ...reply, replies: [] };
      thread.push(node);
      threadByKey.set(key, node);
    }
  });

  return thread;
};

const normalizeFields = ({ name, email, body }) => ({
  name: typeof name === "string" ? name.trim() : "",
  email: typeof email === "string" ? email.trim() : "",
  body:
    typeof body === "string" ? body.trim().replace(/\r\n?/g, "\n") : "",
});

/**
 * Identity is collected once per visit (name + email), not per comment.
 */
export const validateViewerIdentity = ({ name, email }) => {
  const values = normalizeFields({ name, email, body: "" });
  const errors = {};

  if (!values.name) {
    errors.name = "Enter your name.";
  } else if (/[\r\n]/.test(values.name)) {
    errors.name = "Name must be a single line.";
  } else if (characterLength(values.name) > MAX_COMMENT_NAME_LENGTH) {
    errors.name = `Name must be ${MAX_COMMENT_NAME_LENGTH} characters or fewer.`;
  }

  if (!values.email) {
    errors.email = "Enter your email address.";
  } else if (
    values.email.length > MAX_COMMENT_EMAIL_LENGTH ||
    !EMAIL_PATTERN.test(values.email)
  ) {
    errors.email = "Enter a valid email address.";
  }

  return { values: { name: values.name, email: values.email }, errors };
};

/**
 * Per-comment content only — the commenter's identity is supplied separately.
 */
export const validateCommentDraft = (
  fields,
  { bodyLabel = "comment", maxTimestampSeconds, languageCode } = {},
) => {
  const values = normalizeFields({ body: fields?.body });
  const errors = {};

  const language =
    typeof languageCode === "string" && languageCode.trim()
      ? languageCode.trim().toLowerCase()
      : "en";

  const upperBound =
    Number.isSafeInteger(maxTimestampSeconds) && maxTimestampSeconds > 0
      ? Math.min(maxTimestampSeconds, MAX_TIMESTAMP_SECONDS)
      : MAX_TIMESTAMP_SECONDS;

  const category =
    typeof fields?.category === "string" && CATEGORY_VALUES.has(fields.category)
      ? fields.category
      : DEFAULT_COMMENT_CATEGORY;

  let timestampSeconds = null;
  const rawTimestamp = fields?.timestampSeconds;
  if (rawTimestamp !== undefined && rawTimestamp !== null && rawTimestamp !== "") {
    const parsed =
      typeof rawTimestamp === "number"
        ? rawTimestamp
        : parseTimestampInput(String(rawTimestamp));
    if (parsed === null || !Number.isFinite(parsed)) {
      errors.timestampSeconds = "Use mm:ss, or a number of seconds.";
    } else if (parsed < 0 || parsed > upperBound) {
      errors.timestampSeconds = `This video is only ${formatTimestamp(upperBound)} long.`;
    } else {
      timestampSeconds = Math.round(parsed);
    }
  }

  if (!values.body) {
    errors.body = `Enter your ${bodyLabel}.`;
  } else if (characterLength(values.body) > MAX_COMMENT_BODY_LENGTH) {
    errors.body = `Your ${bodyLabel} must be ${MAX_COMMENT_BODY_LENGTH} characters or fewer.`;
  }

  return {
    values: { body: values.body, category, timestampSeconds, languageCode: language },
    errors,
  };
};

export const getPublicCommentSubmitError = (error, fallbackMessage) => {
  const serverMessage = error?.response?.data?.error;
  if (typeof serverMessage === "string" && serverMessage.trim()) {
    return serverMessage.trim();
  }
  if (error?.response?.status === 429) {
    return "Too many comment submissions. Please try again later.";
  }
  return fallbackMessage;
};

export const isCommentsDisabledError = (error) =>
  error?.response?.status === 403 &&
  error.response.data?.commentsEnabled === false;

// Comments are owned by the browser that created them, not by an account. The
// server stores only a SHA-256 hash of this token, so this copy is the only one.
const TOKEN_STORAGE_PREFIX = "ce.videoCommenter.";
const TOKEN_PATTERN = /^[a-f0-9]{64}$/;

const commenterStorage = () => {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
};

export const readCommenterToken = (publicId) => {
  const store = commenterStorage();
  if (!store || !publicId) return null;
  try {
    const value = store.getItem(TOKEN_STORAGE_PREFIX + publicId);
    return value && TOKEN_PATTERN.test(value) ? value : null;
  } catch {
    return null;
  }
};

export const storeCommenterToken = (publicId, token) => {
  const store = commenterStorage();
  if (!store || !publicId) return;
  if (typeof token !== "string" || !TOKEN_PATTERN.test(token)) return;
  try {
    store.setItem(TOKEN_STORAGE_PREFIX + publicId, token);
  } catch {
    // Storage can be unavailable (private mode); commenting still works, the
    // author simply cannot edit from this browser.
  }
};
